<template>
    <div :style="$_getStyle" :class="getObjectDetailClass">
        <!-- Header -->
        <div class="panel-header modeless" v-if="mode === 'panel'">
            <h3 class="panel-header-title modeless">{{ $t('labelLegend') }}</h3>
            <!-- Close icon -->
            <icon-close
                v-if="menuComponents.legend.close.visible"
                id="panel-legend-close"
                @close="closeLegendPanel()"
                iconColor=""
                class="d-inline-block float-right"
            >
            </icon-close>
        </div>
        <!-- Content -->
        <div class="panel-content-sidebar">
            <div class="legendcontainer tab-content-left-marging">
                <div class="legends">
                    <div class="no-print">
                        <x-checkbox
                            v-if="$_showToggleAllLayers"
                            :name="`toggleAllLayers`"
                            :text="
                                hasSelectedItems
                                    ? $t('toggleAllLayersOff')
                                    : $t('toggleAllLayersOn')
                            "
                            :indeterminate="isIndeterminate"
                            class="font-weight-bold"
                            @change="$_toggleAllLayers($event)"
                            :value="hasSelectedItems"
                        >
                        </x-checkbox>
                    </div>
                    <span v-if="webLegendData.length < 1">{{
                        $t('stylesUndefined')
                    }}</span>
                    <b-card
                        no-body
                        v-for="layer in webLegendData"
                        :key="layer.id"
                    >
                        <!--Hide legend entry when not checked in print view -->
                        <div :class="{ 'no-print': !layer.active }">
                            <b-card-header
                                v-b-toggle="`legend${layer.id}`"
                                class="p-0 pr-3 d-flex"
                                data-toggle="collapse"
                            >
                                <x-checkbox
                                    :name="`checkbox-${layer.id}`"
                                    :value="layer.active"
                                    :text="layer.title"
                                    class="font-weight-bold"
                                    :enableClickEvent="true"
                                    @click.stop=""
                                    @change="$_toggleLayer($event, layer.id)"
                                />
                                <!--chevron icon, only when a named style available -->
                                <div class="ml-auto pl-2">
                                    <div class="no-print">
                                        <div class="d-flex">
                                            <!--info-->
                                            <layer-info
                                                :layer="layer"
                                                v-if="
                                                    layer.infoUrl &&
                                                    layer.infoLinkType ===
                                                        INFO_LINK_TYPE.EXTERNAL
                                                "
                                            />
                                            <!--metadata-->
                                            <layer-metadata
                                                :layer="layer"
                                                v-if="
                                                    layer.metadataUrl &&
                                                    layer.infoLinkType ===
                                                        INFO_LINK_TYPE.METADATA
                                                "
                                            />
                                            <div
                                                :ref="`legend_expand_${layer.id}`"
                                            >
                                                <em
                                                    class="fa theme-chevron"
                                                ></em>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </b-card-header>
                            <b-collapse
                                :id="`legend${layer.id}`"
                                :visible="layer.legendExpanded"
                            >
                                <b-card-body>
                                    <style-custom-css
                                        v-if="
                                            layer.isCustomCssObject &&
                                            layer.geometryType
                                        "
                                        :geometryType="
                                            layer.geometryType.split('')
                                        "
                                        :customStylingOptions="
                                            layer.styles[0].customStylingOptions
                                        "
                                        class="pr-3"
                                    />
                                    <style-css
                                        v-if="
                                            layer.isCssObject &&
                                            layer.geometryType
                                        "
                                        :layer="layer"
                                        class="pr-3"
                                    />
                                    <style-image
                                        v-if="layer.isImageObject"
                                        :layer="layer"
                                        class="pr-3"
                                        @imgNotFound="$_onImgNotFound"
                                    />
                                </b-card-body>
                            </b-collapse>
                        </div>
                    </b-card>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters, mapActions } from 'vuex';
    import {
        INTERFACE_PANEL_TRIGGERS,
        MENUBAR_HEIGHT,
        INFO_LINK_TYPE
    } from '../../../../constants';

    import IconClose from './../../../IconClose.vue';
    import StyleCustomCss from './StyleCustomCss.vue';
    import StyleCss from './StyleCss.vue';
    import StyleImage from './StyleImage.vue';
    import LayerInfo from '../layers/LayerInfo';
    import LayerMetadata from '../layers/LayerMetadata';

    export default {
        name: 'legend-panel',
        props: { mode: { type: String, default: 'panel' } },

        components: {
            'icon-close': IconClose,
            'style-css': StyleCss,
            'style-image': StyleImage,
            'style-custom-css': StyleCustomCss,
            'layer-info': LayerInfo,
            'layer-metadata': LayerMetadata
        },
        data() {
            return { INFO_LINK_TYPE: INFO_LINK_TYPE };
        },
        computed: {
            ...mapState(['uiElements']),
            ...mapGetters([
                'getWebLegendData',
                'getActivatedLayers',
                'getAllOrderedLayers',
                'getWebTheme',
                'getDetailInformationPanelHeight'
            ]),
            legendPanel() {
                return this.uiElements[INTERFACE_PANEL_TRIGGERS.LEGEND];
            },
            menuComponents: function () {
                return this.uiElements.menu.components;
            },
            webLegendData() {
                return this.getWebLegendData();
            },
            /**
             * Return the class for legend panel
             * @returns {string} - style
             */
            getObjectDetailClass() {
                if (this.mode === 'accessible') {
                    return { show: true };
                }
                return {
                    show: this.legendPanel.visible,
                    baselayer: this.$store.getters.getBaseLayers.length > 1,
                    'legend-detail': true,
                    shadowSmall: true
                };
            },
            $_getStyle() {
                const resize = this.legendPanel.resize;
                return resize
                    ? `max-height: calc(100% - ${MENUBAR_HEIGHT}px - ${this.getDetailInformationPanelHeight}px - 10px)`
                    : null;
            },
            hasSelectedItems: function () {
                return this.getActivatedLayers.length > 0;
            },
            isIndeterminate: function () {
                return (
                    this.hasSelectedItems &&
                    this.getActivatedLayers.length <
                        this.getAllOrderedLayers.filter(
                            (layer) => layer.type !== 'base'
                        ).length
                );
            },
            $_showToggleAllLayers() {
                return (
                    this.webLegendData.length > 1 &&
                    this.getWebTheme?.data?.showHideAllLayers
                );
            }
        },
        methods: {
            ...mapActions(['togglePanel']),
            closeLegendPanel() {
                this.togglePanel({
                    panelName: INTERFACE_PANEL_TRIGGERS.LEGEND,
                    visible: false
                });
            },
            $_toggleAllLayers(input) {
                this.$store.dispatch('toggleAllLayers', {
                    mapName: 'main',
                    active: input
                });
            },
            $_toggleLayer(input, id) {
                this.$store.dispatch('toggleLayer', {
                    mapName: 'main',
                    id,
                    active: input
                });
            },
            // Hide expand/collapse button if an error occurs when loading the image
            $_onImgNotFound(id) {
                const span = this.$refs[`legend_expand_${id}`];
                if (span && span.length === 1) {
                    span[0].style.display = 'none';
                }
            }
        }
    };
</script>

<style scoped lang="scss">
.panel-content-sidebar {
    flex: 1;
    display: flex !important;
    flex-direction: column;
    overflow: auto;
}

.legend-detail {
    @include z-index(legend-panel);
    position: absolute;
    top: $menu-bar-height;
    left: -470px;
    min-width: 230px;
    max-width: $default-panel-width;
    background-color: #fff;
    -moz-transition: all 0.3s ease-in-out;
    -o-transition: all 0.3s ease-in-out;
    transition: all 0.3s ease-in-out;
    display: flex !important;
    flex-direction: column;
    max-height: calc(100% - $menu-bar-height - 10px);

    &.show {
        @include margin-left-navbar;
        position: absolute;
        top: $menu-bar-height;
        left: 10px;
    }

    &.baselayer {
        max-height: calc(100% - 155px);
    }
}

.legendcontainer {
    margin-bottom: 15px;
}

.card {
    border: 0px !important;

    .card-header {
        background-color: rgba(0, 0, 0, 0) !important;
        border-bottom: 0px !important;
        cursor: pointer;
    }

    .card-body {
        padding: 0.15rem 1.25rem 0rem 1.4rem;
    }
}

/*-------------------------------------
    max-width : 480px
    ---------------------------------------*/
@media (max-width: 480px) {
    .legend-detail.show {
        width: calc(100% - 20px);
    }
}
</style>
